/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import RadarOutlinedIcon from "@mui/icons-material/RadarOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  demoAssets,
  demoCredentials,
  demoDomain,
  demoFindings,
  demoTrend,
} from "data/demoFindings";

const stages = {
  EMPTY: "empty",
  FORM: "form",
  SCANNING: "scanning",
  RESULTS: "results",
  TOUR: "tour",
  COMPLETE: "complete",
};

const nav = [
  ["overview", "Overview", DashboardOutlinedIcon],
  ["risks", "Risks", WarningAmberOutlinedIcon],
  ["assets", "Assets", DnsOutlinedIcon],
  ["credentials", "Credentials", KeyOutlinedIcon],
  ["monitoring", "Monitoring", RadarOutlinedIcon],
  ["reports", "Reports", DescriptionOutlinedIcon],
  ["settings", "Settings", SettingsOutlinedIcon],
];

const bg =
  "radial-gradient(circle at 15% 0%, rgba(124,58,237,0.18), transparent 26rem), radial-gradient(circle at 86% 0%, rgba(31,119,180,0.12), transparent 34rem), linear-gradient(135deg, #020814 0%, #031020 48%, #06172b 100%)";
const panel = {
  borderRadius: "10px",
  border: "1px solid rgba(74,104,143,0.44)",
  background:
    "linear-gradient(135deg, rgba(15,28,48,0.95), rgba(4,16,31,0.96)), radial-gradient(circle at 12% 30%, rgba(255,255,255,0.05), transparent 18rem)",
  color: "#f8fbff",
  boxShadow: "0 20px 70px rgba(0,0,0,0.28)",
};
const button = {
  minHeight: 46,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 900,
  fontSize: 15,
};
const outline = {
  ...button,
  color: "#f8fbff",
  border: "1px solid rgba(148,163,184,0.34)",
  background: "rgba(255,255,255,0.04)",
  "&:hover": { borderColor: "rgba(248,251,255,0.54)", background: "rgba(255,255,255,0.08)" },
};
const primary = {
  ...button,
  color: "#fff",
  border: "1px solid rgba(139,92,246,0.7)",
  background: "linear-gradient(135deg, #7c3aed, #4c1d95)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16)",
  "&:hover": { background: "linear-gradient(135deg, #8b5cf6, #5b21b6)" },
  "&.Mui-disabled": { color: "rgba(255,255,255,0.45)", background: "rgba(124,58,237,0.42)" },
};
const compactDesktop = "@media (min-width: 1024px) and (max-height: 1100px)";
const riskTableColumns =
  "90px minmax(300px, 2.2fr) minmax(210px, 1.35fr) minmax(200px, 1.2fr) 140px";
const riskCell = { px: 2, py: 2.2, minWidth: 0, overflowWrap: "anywhere" };
const tableHeaderSx = {
  display: "grid",
  alignItems: "start",
  background: "rgba(30,54,91,0.82)",
  color: "#c9d7ef",
  fontSize: 12,
  fontWeight: 900,
  textTransform: "uppercase",
};
const tableRowSx = {
  display: "grid",
  alignItems: "start",
  background: "rgba(8,23,43,0.72)",
  borderTop: "1px solid rgba(148,163,184,0.16)",
  color: "#d8e7ff",
};
const guideText = { color: "rgba(248,251,255,0.78)", mt: 1.1, lineHeight: 1.65 };
const fieldSx = {
  input: { color: "#fff" },
  textarea: { color: "#fff" },
  label: { color: "rgba(248,251,255,0.62)" },
  fieldset: { borderColor: "rgba(148,163,184,0.32)" },
  "& .MuiSelect-select": { color: "#fff" },
  "& .MuiInputBase-root": { minHeight: 44 },
};

const blankForm = { company: "", website: "", domains: "", ips: "", email: "" };
const typedCompany = "Example Ltd";
const typedDomain = demoDomain || "example.com";
const typingTotal = typedCompany.length + typedDomain.length;
const scanSteps = [
  "Checking domains",
  "Finding exposed services",
  "Reviewing email security",
  "Checking known breach exposure",
  "Prioritising business risk",
];
const discoveryStatuses = [
  "Checking domain ownership signals...",
  "Finding exposed services...",
  "Reviewing email security...",
  "Checking public breach indicators...",
  "Mapping external assets...",
  "Prioritising business risk...",
  "Generating risks...",
];
const discoveryNodes = [
  { label: "Username Enumeration", x: 14, y: 18, step: 0 },
  { label: "Email Addresses", x: 79, y: 16, step: 0 },
  { label: "Domain Names", x: 24, y: 70, step: 0 },
  { label: "IP Addresses", x: 71, y: 75, step: 1 },
  { label: "WHOIS Lookups", x: 44, y: 10, step: 1 },
  { label: "DNS Settings", x: 88, y: 42, step: 1 },
  { label: "Website Exposure", x: 9, y: 48, step: 2 },
  { label: "Vulnerability Scanning", x: 55, y: 88, step: 2 },
  { label: "Intel", x: 31, y: 32, step: 2 },
  { label: "Public Records", x: 83, y: 62, step: 3 },
  { label: "Data Breaches", x: 18, y: 84, step: 3 },
  { label: "Credential Exposure", x: 65, y: 27, step: 3 },
  { label: "Tailored Risks", x: 41, y: 78, step: 4 },
  { label: "Generating Risks...", x: 58, y: 50, step: 4 },
];
const tourSteps = [
  {
    title: "Exposure score",
    text: "This score gives a simple view of how exposed the company is from the internet. Higher scores mean attackers have more realistic paths to business impact.",
    target: "score",
    placement: "rightTop",
  },
  {
    title: "What matters first",
    text: "The most urgent issue is shown first. In this demo, an exposed company password is prioritised because it can lead directly to account takeover.",
    target: "first-finding",
    placement: "rightMiddle",
  },
  {
    title: "Understand the issue",
    text: "View details explains what was found, why it matters, the evidence, and the likely downstream impact.",
    target: "view-details",
    placement: "nearActions",
  },
  {
    title: "See how to fix it",
    text: "This opens the remediation view so the user can see the exact actions to take next.",
    target: "fix",
    placement: "nearActions",
  },
  {
    title: "Tailored remediation steps",
    text: "This is the practical fix list. It converts technical findings into actions that can be followed internally or shared with an IT provider.",
    target: "remediation",
    placement: "rightTop",
  },
  {
    title: "View all findings",
    text: "The Risks page shows the full prioritised list so users can review every issue after the guided overview.",
    target: "risks-list",
    placement: "rightTop",
  },
];

function severityColor(severity) {
  if (severity === "Critical") return "#ff4d57";
  if (severity === "High") return "#ff7a18";
  if (severity === "Medium") return "#f7c948";
  return "#69e3ff";
}

function getTypedForm(progress) {
  const companyChars = Math.min(progress, typedCompany.length);
  const domainChars = Math.max(0, Math.min(progress - typedCompany.length, typedDomain.length));
  return {
    ...blankForm,
    company: typedCompany.slice(0, companyChars),
    domains: typedDomain.slice(0, domainChars),
  };
}

function getDomainLabel(domain) {
  const clean = String(domain || "")
    .split(/[\s,]+/)
    .find(Boolean);
  return clean || typedDomain || "your-company.com";
}

function scrollToDemoTarget(target) {
  if (typeof document === "undefined" || !target) return;
  document.querySelector(`[data-demo-target="${target}"]`)?.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });
}

function goToHomepage() {
  if (typeof window !== "undefined") window.location.assign("/");
}

function SeverityBadge({ severity }) {
  const color = severityColor(severity);
  return (
    <Chip
      size="small"
      label={String(severity).toUpperCase()}
      sx={{
        color,
        height: 30,
        borderRadius: "7px",
        border: `2px solid ${color}`,
        background: `${color}10`,
        fontSize: 14,
        fontWeight: 900,
      }}
    />
  );
}

function MiniTrendGraph({
  data,
  metric = "vulnerabilities",
  color = "#5ff3ff",
  height = 86,
  showYAxis = false,
}) {
  const values = data.map((item) => item[metric]);
  const max = Math.max(...values, 1);
  const ticks = [max, Math.round(max / 2), 0];
  const points = values
    .map((value, index) => {
      const x = values.length === 1 ? 50 : (index / (values.length - 1)) * 100;
      const y = 92 - (value / max) * 76;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: showYAxis ? "42px 1fr" : "1fr",
        gap: showYAxis ? 1.2 : 0,
        height,
        minWidth: 0,
      }}
    >
      {showYAxis && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            color: "rgba(248,251,255,0.58)",
            fontSize: 12,
            fontWeight: 800,
            py: 0.4,
            textAlign: "right",
          }}
        >
          {ticks.map((tick) => (
            <span key={tick}>{tick}</span>
          ))}
        </Box>
      )}
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%">
        {showYAxis &&
          [16, 54, 92].map((y) => (
            <line
              key={y}
              x1="0"
              x2="100"
              y1={y}
              y2={y}
              stroke="rgba(148,163,184,0.18)"
              strokeWidth="1"
            />
          ))}
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {values.map((value, index) => {
          const x = values.length === 1 ? 50 : (index / (values.length - 1)) * 100;
          const y = 92 - (value / max) * 76;
          return <circle key={`${metric}-${index}`} cx={x} cy={y} r="2.8" fill={color} />;
        })}
      </svg>
    </Box>
  );
}

function DataTable({ columns, rows, minWidth = 760, rowSx }) {
  return (
    <Box
      sx={{ overflowX: "auto", borderRadius: "8px", border: "1px solid rgba(148,163,184,0.22)" }}
    >
      <Box sx={{ minWidth }}>
        <Box
          sx={{
            ...tableHeaderSx,
            gridTemplateColumns: columns.map((column) => column.width).join(" "),
          }}
        >
          {columns.map((column) => (
            <Box key={column.header} sx={riskCell}>
              {column.header}
            </Box>
          ))}
        </Box>
        {rows.map((row) => (
          <Box
            key={row.id}
            sx={{
              ...tableRowSx,
              gridTemplateColumns: columns.map((column) => column.width).join(" "),
              ...(typeof rowSx === "function" ? rowSx(row) : rowSx),
            }}
          >
            {columns.map((column) => (
              <Box key={column.header} sx={riskCell}>
                {column.render(row)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function exportFindingsCsv(rows) {
  const headers = ["Score", "Vulnerability", "Category", "Host", "Severity"];
  const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const csv = [
    headers.join(","),
    ...rows.map((finding) =>
      [finding.numericScore, finding.title, finding.category, finding.host, finding.severity]
        .map(escape)
        .join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "netdefenders-demo-risks.csv";
  link.click();
  URL.revokeObjectURL(url);
}

function HighlightDot({ active, label = "Look here", sx = {} }) {
  if (!active) return null;
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "absolute",
        zIndex: 20,
        display: "grid",
        placeItems: "center",
        pointerEvents: "none",
        ...sx,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: 54,
          height: 54,
          borderRadius: "50%",
          border: "2px solid rgba(95,243,255,0.92)",
          animation: "ndPulse 1.4s ease-out infinite",
          boxShadow: "0 0 28px rgba(95,243,255,0.42)",
        }}
      />
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "#5ff3ff",
          border: "3px solid #061020",
          boxShadow: "0 0 0 5px rgba(95,243,255,0.25), 0 0 26px rgba(95,243,255,0.7)",
        }}
      />
      <Typography
        sx={{
          position: "absolute",
          top: 34,
          whiteSpace: "nowrap",
          color: "#061020",
          background: "#5ff3ff",
          borderRadius: "999px",
          px: 1.2,
          py: 0.35,
          fontSize: 11,
          fontWeight: 900,
          boxShadow: "0 12px 28px rgba(0,0,0,0.28)",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function CtaButton({ navigate, label = "Start Free Trial", highlighted = false }) {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <Button
        sx={{
          ...primary,
          minWidth: 170,
          borderColor: highlighted ? "#5ff3ff" : primary.border,
          boxShadow: highlighted
            ? "0 0 0 4px rgba(95,243,255,0.12), inset 0 1px 0 rgba(255,255,255,0.16)"
            : primary.boxShadow,
        }}
        onClick={() => navigate("/signup?source=demo")}
      >
        {label}
      </Button>
      <HighlightDot
        active={highlighted}
        label="Start"
        sx={{ right: -4, top: -2, transform: "translate(50%, -50%)" }}
      />
    </Box>
  );
}

function HomeButton({ compact = false }) {
  return (
    <Button
      startIcon={compact ? null : <HomeOutlinedIcon />}
      onClick={goToHomepage}
      sx={{
        ...outline,
        minWidth: compact ? 44 : 170,
        width: compact ? 44 : "auto",
        height: compact ? 44 : "auto",
        px: compact ? 0 : 2.4,
        color: "#fff",
        border: "2px solid #ff7a18",
        background: "rgba(255,122,24,0.02)",
        boxShadow: "none",
        fontSize: compact ? 0 : 15,
        fontWeight: 900,
        letterSpacing: 0.2,
        textTransform: "uppercase",
        "&:hover": {
          borderColor: "#ff9b45",
          background: "rgba(255,122,24,0.1)",
        },
      }}
    >
      {compact ? <HomeOutlinedIcon /> : "CLOSE DEMO"}
    </Button>
  );
}

function TopBar({ title, subtitle, navigate, tourTarget = "", mobileHidden = false }) {
  return (
    <Box
      sx={{
        display: mobileHidden ? { xs: "none", md: "flex" } : "flex",
        justifyContent: "space-between",
        gap: 2,
        mb: 0.6,
        flexWrap: "wrap",
      }}
    >
      <Box>
        <Typography sx={{ color: "#fff", fontSize: { xs: 31, md: 36 }, fontWeight: 900 }}>
          {title}
        </Typography>
        <Typography sx={{ color: "rgba(248,251,255,0.78)", fontSize: 18, mt: 0.7 }}>
          {subtitle}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.4, flexWrap: "wrap" }}>
        <HomeButton />
        <CtaButton navigate={navigate} highlighted={tourTarget === "trial"} />
      </Box>
    </Box>
  );
}

function MobileHeader({ title, onMenu, hidden = false }) {
  if (hidden) return null;
  return (
    <Box
      sx={{
        display: { xs: "grid", lg: "none" },
        gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
        alignItems: "center",
        gap: 1,
        mb: 2,
        minHeight: 48,
      }}
    >
      <Box sx={{ minWidth: 0, display: "flex", alignItems: "center" }}>
        <Box
          component="img"
          src="/demo/netdefenders-brand/netdefenders-white-horizontal.png"
          alt="NetDefenders"
          sx={{ width: 132, maxWidth: "100%" }}
        />
      </Box>
      <Typography
        sx={{
          color: "#fff",
          fontSize: 17,
          fontWeight: 900,
          textAlign: "center",
          lineHeight: 1.15,
          maxWidth: 156,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.75 }}>
        <Button
          aria-label="Back to NetDefenders home"
          onClick={goToHomepage}
          sx={{
            minWidth: 44,
            width: 44,
            height: 44,
            p: 0,
            borderRadius: "8px",
            color: "#f8fbff",
            border: "2px solid #ff7a18",
            background: "rgba(255,122,24,0.02)",
            "&:hover": {
              borderColor: "#ff9b45",
              background: "rgba(255,122,24,0.1)",
            },
          }}
        >
          <HomeOutlinedIcon />
        </Button>
        <Button
          aria-label="Open menu"
          onClick={onMenu}
          sx={{
            minWidth: 44,
            width: 44,
            height: 44,
            p: 0,
            borderRadius: "8px",
            color: "#f8fbff",
            border: "1px solid rgba(148,163,184,0.34)",
            background: "rgba(255,255,255,0.04)",
            "&:hover": {
              borderColor: "rgba(248,251,255,0.54)",
              background: "rgba(255,255,255,0.08)",
            },
          }}
        >
          <MenuOutlinedIcon />
        </Button>
      </Box>
    </Box>
  );
}

function Sidebar({ current, onChange, mobile = false, tourTarget = "", locked = false }) {
  return (
    <Box
      sx={{
        width: mobile ? 306 : { xs: "100%", lg: 286 },
        boxSizing: "border-box",
        px: 2.5,
        py: 4,
        display: "grid",
        gap: 1,
        alignContent: "start",
        [compactDesktop]: { py: 2.5, gap: 0.25 },
      }}
    >
      <Box sx={{ mb: 3.8, [compactDesktop]: { mb: 2.4 } }}>
        <Box
          component="img"
          src="/portal/netdefenders-brand/netdefenders-white-horizontal.png"
          alt="NetDefenders"
          sx={{ width: 207, maxWidth: "100%" }}
        />
        <Typography sx={{ color: "rgba(248,251,255,0.68)", mt: 0.5, fontSize: 13 }}>
          External Exposure Monitoring
        </Typography>
      </Box>
      {nav.map(([key, label, Icon]) => {
        const active = current === key;
        const highlighted = tourTarget === "risks-list" && key === "risks";
        const disabled = locked && key !== "overview";
        return (
          <Box key={key} sx={{ position: "relative" }}>
            <Button
              fullWidth
              disabled={disabled}
              startIcon={<Icon />}
              onClick={() => !disabled && onChange(key)}
              sx={{
                justifyContent: "flex-start",
                minHeight: 53,
                px: 1.8,
                borderRadius: "8px",
                color: disabled
                  ? "rgba(248,251,255,0.3) !important"
                  : active || highlighted
                  ? "#fff"
                  : "rgba(248,251,255,0.78)",
                textTransform: "none",
                fontSize: 16,
                fontWeight: active || highlighted ? 800 : 500,
                background: active ? "linear-gradient(135deg, #7b2cff, #4d18b8)" : "transparent",
                border: highlighted ? "2px solid #5ff3ff" : "2px solid transparent",
                boxShadow: highlighted ? "0 0 0 4px rgba(95,243,255,0.12)" : "none",
                opacity: disabled ? 0.56 : 1,
                cursor: disabled ? "not-allowed" : "pointer",
                [compactDesktop]: { minHeight: 42, fontSize: 15 },
              }}
            >
              {label}
            </Button>
            <HighlightDot
              active={highlighted}
              label="Next"
              sx={{ right: 22, top: 22, transform: "translate(50%, -50%)" }}
            />
          </Box>
        );
      })}
      <Box sx={{ ...panel, p: 2.5, mt: 6.8, [compactDesktop]: { display: "none" } }}>
        <ShieldOutlinedIcon sx={{ color: "#a78bfa" }} />
        <Typography sx={{ color: "#fff", fontWeight: 900, mt: 1 }}>Built for action</Typography>
        <Typography sx={{ color: "rgba(248,251,255,0.72)", fontSize: 14, lineHeight: 1.55 }}>
          Prioritise the issues that reduce business risk fastest, then share clear fixes with your
          IT provider.
        </Typography>
      </Box>
    </Box>
  );
}

function EmptyState({ onStart, navigate }) {
  return (
    <Box sx={{ display: "grid", gap: 2.2 }}>
      <TopBar
        title="NetDefenders Live Demo"
        subtitle="Start with no data, run guided discovery, then reveal the exposure dashboard."
        navigate={navigate}
        mobileHidden
      />
      <Box
        sx={{
          ...panel,
          p: { xs: 3, md: 5 },
          minHeight: 450,
          display: "grid",
          alignContent: "center",
        }}
      >
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Chip
              label="GUIDED DEMO"
              sx={{
                color: "#5ff3ff",
                border: "1px solid #5ff3ff",
                background: "rgba(95,243,255,0.08)",
                fontWeight: 900,
              }}
            />
            <Typography
              sx={{
                color: "#fff",
                fontSize: { xs: 36, md: 54 },
                lineHeight: 1.02,
                fontWeight: 900,
                mt: 2,
              }}
            >
              No exposure data yet
            </Typography>
            <Typography
              sx={{
                color: "rgba(248,251,255,0.76)",
                mt: 2,
                fontSize: 18,
                lineHeight: 1.65,
                maxWidth: 680,
              }}
            >
              Start a guided discovery to see how NetDefenders finds exposed assets, prioritises
              business risk and explains what to fix first.
            </Typography>
            <Box sx={{ display: "flex", gap: 1.4, flexWrap: "wrap", mt: 3 }}>
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <Button
                  sx={{ ...primary, minWidth: 180 }}
                  onClick={onStart}
                  endIcon={<ArrowForwardIcon />}
                >
                  Get Started
                </Button>
                <HighlightDot
                  active
                  label="Begin"
                  sx={{ right: -2, top: -2, transform: "translate(50%, -50%)" }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ ...panel, p: 3, borderColor: "rgba(95,243,255,0.36)" }}>
              {[
                "Enter company details",
                "Confirm testing authority",
                "Run futuristic discovery",
                "Reveal prioritised findings",
              ].map((item, index) => (
                <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 1.6, py: 1.3 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      background: "rgba(124,58,237,0.22)",
                      border: "1px solid rgba(167,139,250,0.58)",
                      fontWeight: 900,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography sx={{ color: "#fff", fontWeight: 800 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

function guidePosition(placement) {
  const base = {
    position: "fixed",
    zIndex: 1401,
    width: "min(440px, calc(100vw - 32px))",
    maxHeight: "calc(100vh - 32px)",
    overflow: "auto",
  };
  if (placement === "rightTop")
    return { ...base, top: { xs: 86, md: 92 }, right: { xs: 16, md: 28 } };
  if (placement === "rightMiddle")
    return {
      ...base,
      top: { xs: "auto", md: 235 },
      bottom: { xs: 16, md: "auto" },
      right: { xs: 16, md: 28 },
    };
  if (placement === "leftBottom")
    return { ...base, left: { xs: 16, md: 304 }, bottom: { xs: 16, md: 28 } };
  if (placement === "nearActions")
    return {
      ...base,
      top: { xs: "auto", md: 338 },
      bottom: { xs: 16, md: "auto" },
      right: { xs: 16, md: 326 },
    };
  return { ...base, top: { xs: 16, md: 28 }, right: { xs: 16, md: 28 } };
}

function GuideCard({ title, stepLabel, placement, children, isSmallMobile = false }) {
  const mobileSheet = {
    position: "fixed",
    left: 12,
    right: 12,
    bottom: 12,
    width: "auto",
    maxHeight: "34vh",
    overflow: "auto",
    zIndex: 1401,
  };
  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 1400, pointerEvents: "none" }}>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: isSmallMobile ? "transparent" : "rgba(1,6,14,0.28)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          ...panel,
          ...(isSmallMobile ? mobileSheet : guidePosition(placement)),
          p: isSmallMobile ? 2 : { xs: 2.5, md: 3 },
          borderColor: "rgba(95,243,255,0.65)",
          background: isSmallMobile
            ? "linear-gradient(135deg, rgba(15,28,48,0.94), rgba(4,16,31,0.96))"
            : panel.background,
          boxShadow: "0 22px 90px rgba(0,0,0,0.55), 0 0 0 1px rgba(95,243,255,0.18)",
          pointerEvents: "auto",
        }}
      >
        <Typography
          sx={{ color: "#5ff3ff", fontSize: 13, fontWeight: 900, textTransform: "uppercase" }}
        >
          {stepLabel}
        </Typography>
        <Typography
          sx={{
            color: "#fff",
            fontSize: isSmallMobile ? 24 : { xs: 26, md: 32 },
            fontWeight: 900,
            mt: isSmallMobile ? 0.45 : 0.8,
          }}
        >
          {title}
        </Typography>
        {children}
      </Box>
    </Box>
  );
}

function SetupGuide({
  setupStep,
  typingStarted,
  typingDone,
  authorised,
  onProceed,
  isSmallMobile,
}) {
  const mobileText = { ...guideText, fontSize: 15, lineHeight: 1.45, mt: 0.8 };
  if (setupStep === 0) {
    return (
      <GuideCard
        title={isSmallMobile ? "Enter example details" : "Enter what you know"}
        stepLabel="GETTING STARTED"
        placement="rightTop"
        isSmallMobile={isSmallMobile}
      >
        {isSmallMobile ? (
          <Typography sx={mobileText}>
            We will type example company details for you. Tap Proceed to continue.
          </Typography>
        ) : (
          <>
            <Typography sx={guideText}>
              Enter any information you currently know about your business. Do not worry if you do
              not know something.
            </Typography>
            <Typography sx={guideText}>
              For this demo, we will automatically type an example company name and domain so you
              can see how the flow works.
            </Typography>
          </>
        )}
        <Button
          sx={{ ...primary, mt: isSmallMobile ? 1.4 : 2, color: "#fff !important" }}
          onClick={onProceed}
          disabled={typingStarted && !typingDone}
        >
          {typingStarted && !typingDone ? "Typing example..." : "Proceed"}
        </Button>
      </GuideCard>
    );
  }
  if (setupStep === 1) {
    return (
      <GuideCard
        title="Confirm authorisation"
        stepLabel="GETTING STARTED"
        placement="rightMiddle"
        isSmallMobile={isSmallMobile}
      >
        <Typography sx={isSmallMobile ? mobileText : guideText}>
          {isSmallMobile
            ? "Tick the checkbox to confirm you are authorised to test these assets."
            : "Confirm that you have authority to request security testing for the assets listed."}
        </Typography>
        <Typography
          sx={{
            ...(isSmallMobile ? mobileText : guideText),
            color: authorised ? "#5ff3ff" : "rgba(248,251,255,0.74)",
            fontWeight: 900,
          }}
        >
          {authorised
            ? "Authority confirmed. Now click Run Discovery."
            : "Tick the confirmation checkbox to continue."}
        </Typography>
        {isSmallMobile && (
          <Button
            sx={{ ...outline, mt: 1.2 }}
            onClick={() => scrollToDemoTarget("authorisation-checkbox")}
          >
            Show checkbox
          </Button>
        )}
      </GuideCard>
    );
  }
  if (setupStep === 2 && isSmallMobile) {
    return (
      <GuideCard
        title="Run discovery"
        stepLabel="GETTING STARTED"
        placement="rightMiddle"
        isSmallMobile={isSmallMobile}
      >
        <Typography sx={mobileText}>Now run the simulated external exposure discovery.</Typography>
        <Button
          sx={{ ...outline, mt: 1.2 }}
          onClick={() => scrollToDemoTarget("run-discovery-button")}
        >
          Show Run Discovery
        </Button>
      </GuideCard>
    );
  }
  return null;
}

function MobileSetupPrompt({ title, body, actionLabel, actionTarget }) {
  return (
    <Box
      sx={{
        ...panel,
        p: 1.8,
        borderColor: "rgba(95,243,255,0.72)",
        background: "rgba(3,15,30,0.9)",
        boxShadow: "0 0 0 1px rgba(95,243,255,0.16), 0 18px 46px rgba(0,0,0,0.28)",
      }}
    >
      <Typography
        sx={{
          color: "#5ff3ff",
          fontSize: 12,
          fontWeight: 900,
          textTransform: "uppercase",
        }}
      >
        Getting started
      </Typography>
      <Typography sx={{ color: "#fff", fontSize: 24, fontWeight: 900, mt: 0.45 }}>
        {title}
      </Typography>
      <Typography sx={{ color: "rgba(248,251,255,0.78)", fontSize: 15, lineHeight: 1.45, mt: 0.7 }}>
        {body}
      </Typography>
      {actionLabel && (
        <Button
          sx={{ ...outline, mt: 1.2, minHeight: 40 }}
          onClick={() => scrollToDemoTarget(actionTarget)}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
}

function SetupForm({
  form,
  setForm,
  authorised,
  setAuthorised,
  onRun,
  navigate,
  setupStep,
  typingStarted,
  typingField,
  typingDone,
  onProceed,
  isSmallMobile,
}) {
  const canRun = authorised && form.company.trim() && form.domains.trim();
  const setValue = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));
  const companyFocused = typingStarted && typingField === "company";
  const domainFocused = typingStarted && typingField === "domains";
  const checkboxFocused = setupStep === 1 && !authorised;
  const mobileTypingFocus = isSmallMobile && setupStep === 0 && typingStarted && !typingDone;
  const mobileActionFocus = isSmallMobile && setupStep > 0;
  const hideOptionalMobileFields = mobileTypingFocus || mobileActionFocus;

  useEffect(() => {
    if (!mobileTypingFocus) return undefined;
    const timer = window.setTimeout(() => {
      scrollToDemoTarget(typingField === "domains" ? "domain-name-field" : "company-name-field");
    }, 80);
    return () => window.clearTimeout(timer);
  }, [mobileTypingFocus, typingField]);

  return (
    <Box
      sx={{
        display: "grid",
        gap: mobileTypingFocus ? 1.2 : 2.2,
        position: "relative",
      }}
    >
      {!mobileTypingFocus && (
        <TopBar
          title="Start Discovery"
          subtitle="Use example details to move through the guided product flow."
          navigate={navigate}
          mobileHidden
        />
      )}
      {mobileTypingFocus && (
        <Box
          sx={{
            py: 1,
            px: 0.5,
            minHeight: "calc(100vh - 48px)",
            display: "grid",
            alignContent: "center",
            gap: 1.4,
          }}
        >
          <Typography sx={{ color: "#5ff3ff", fontSize: 13, fontWeight: 900 }}>
            TYPING EXAMPLE DETAILS
          </Typography>
          <Typography sx={{ color: "#fff", fontSize: 30, lineHeight: 1.05, fontWeight: 900 }}>
            Watch the setup fields fill in
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          ...panel,
          p: mobileTypingFocus ? 2 : { xs: 2.5, md: 4 },
          position: "relative",
          ...(mobileTypingFocus && {
            mt: "-48vh",
            borderColor: "rgba(95,243,255,0.72)",
            boxShadow: "0 0 0 1px rgba(95,243,255,0.2), 0 24px 90px rgba(0,0,0,0.52)",
          }),
        }}
      >
        <Grid container spacing={2.2}>
          <Grid item xs={12} md={6}>
            <Box
              data-demo-target="company-name-field"
              sx={{
                position: "relative",
                transform: companyFocused
                  ? isSmallMobile
                    ? "scale(1.045)"
                    : "scale(1.025)"
                  : "scale(1)",
                transition: "transform 220ms ease",
                zIndex: companyFocused ? 3 : 1,
                transformOrigin: "center",
              }}
            >
              <TextField
                fullWidth
                label="Company name"
                value={form.company}
                onChange={setValue("company")}
                placeholder="Example Ltd"
                sx={fieldSx}
              />
              <HighlightDot active={companyFocused} label="Typing" sx={{ right: 16, top: 12 }} />
            </Box>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: hideOptionalMobileFields ? "none" : "block" }}>
            <TextField
              fullWidth
              label="Website URL"
              value={form.website}
              onChange={setValue("website")}
              placeholder="https://example.com"
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              data-demo-target="domain-name-field"
              sx={{
                position: "relative",
                transform: domainFocused
                  ? isSmallMobile
                    ? "scale(1.045)"
                    : "scale(1.025)"
                  : "scale(1)",
                transition: "transform 220ms ease",
                zIndex: domainFocused ? 3 : 1,
                transformOrigin: "center",
              }}
            >
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Domain names"
                value={form.domains}
                onChange={setValue("domains")}
                placeholder="example.com"
                sx={fieldSx}
              />
              <HighlightDot active={domainFocused} label="Typing" sx={{ right: 16, top: 16 }} />
            </Box>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: hideOptionalMobileFields ? "none" : "block" }}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Known IP addresses"
              value={form.ips}
              onChange={setValue("ips")}
              placeholder="203.0.113.10"
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: hideOptionalMobileFields ? "none" : "block" }}>
            <TextField
              fullWidth
              label="Business email"
              value={form.email}
              onChange={setValue("email")}
              placeholder="security@example.com"
              sx={fieldSx}
            />
          </Grid>
          {isSmallMobile && setupStep === 1 && (
            <Grid item xs={12}>
              <MobileSetupPrompt
                title="Confirm authorisation"
                body="Tick the checkbox below to confirm you are authorised to test these assets."
                actionLabel="Show checkbox"
                actionTarget="authorisation-checkbox"
              />
            </Grid>
          )}
          <Grid item xs={12} sx={{ display: mobileTypingFocus ? "none" : "block" }}>
            <Box
              data-demo-target="authorisation-checkbox"
              sx={{
                ...panel,
                p: 2.4,
                background: "rgba(3,15,30,0.86)",
                position: "relative",
                borderColor: checkboxFocused ? "#5ff3ff" : "rgba(74,104,143,0.44)",
              }}
            >
              <HighlightDot active={checkboxFocused} label="Confirm" sx={{ left: 24, top: 24 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={authorised}
                    onChange={(event) => setAuthorised(event.target.checked)}
                    sx={{ color: "#5ff3ff", "&.Mui-checked": { color: "#5ff3ff" } }}
                  />
                }
                label="I confirm I am authorised to request security testing for these assets."
                sx={{ color: "rgba(248,251,255,0.86)", alignItems: "flex-start" }}
              />
              <Typography
                sx={{
                  color: "rgba(248,251,255,0.58)",
                  fontSize: 13,
                  ml: { xs: 0, sm: 4 },
                  mt: 0.8,
                }}
              >
                This demo does not transmit data or start real scanning. It simulates the workflow
                only.
              </Typography>
            </Box>
          </Grid>
          {isSmallMobile && setupStep === 2 && (
            <Grid item xs={12}>
              <MobileSetupPrompt
                title="Run discovery"
                body="Now run the simulated external exposure discovery."
                actionLabel="Show Run Discovery"
                actionTarget="run-discovery-button"
              />
            </Grid>
          )}
          <Grid item xs={12} sx={{ display: mobileTypingFocus ? "none" : "block" }}>
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Box
                data-demo-target="run-discovery-button"
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Button
                  sx={{ ...primary, minWidth: 190, width: { xs: "100%", sm: "auto" } }}
                  disabled={!canRun}
                  onClick={onRun}
                  endIcon={<ArrowForwardIcon />}
                >
                  Run Discovery
                </Button>
                <HighlightDot
                  active={setupStep === 2 && canRun}
                  label="Run"
                  sx={{ right: -2, top: -2, transform: "translate(50%, -50%)" }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <SetupGuide
        setupStep={
          isSmallMobile && (setupStep > 0 || (setupStep === 0 && typingStarted))
            ? null
            : setupStep < 2 || isSmallMobile
            ? setupStep
            : null
        }
        typingStarted={typingStarted}
        typingDone={typingDone}
        authorised={authorised}
        onProceed={onProceed}
        isSmallMobile={isSmallMobile}
      />
    </Box>
  );
}

function DiscoveryGraph({ domain, stepIndex }) {
  const activeNodes = discoveryNodes.filter((node) => node.step <= stepIndex);
  const status = discoveryStatuses[Math.min(stepIndex + 1, discoveryStatuses.length - 1)];
  const mobileNodePositions = {
    "Username Enumeration": { x: 20, y: 16 },
    "Email Addresses": { x: 78, y: 16 },
    "Domain Names": { x: 22, y: 78 },
    "IP Addresses": { x: 76, y: 80 },
    "WHOIS Lookups": { x: 50, y: 9 },
    "DNS Settings": { x: 84, y: 44 },
    "Website Exposure": { x: 16, y: 48 },
    "Vulnerability Scanning": { x: 54, y: 91 },
    Intel: { x: 30, y: 32 },
    "Public Records": { x: 82, y: 66 },
    "Data Breaches": { x: 18, y: 91 },
    "Credential Exposure": { x: 66, y: 30 },
    "Tailored Risks": { x: 40, y: 82 },
    "Generating Risks...": { x: 58, y: 54 },
  };
  return (
    <Box
      sx={{
        position: "relative",
        minHeight: { xs: 430, md: 520 },
        overflow: "visible",
        borderRadius: "14px",
        border: "1px solid rgba(95,243,255,0.24)",
        background:
          "radial-gradient(circle at 50% 50%, rgba(95,243,255,0.12), transparent 12rem), radial-gradient(circle at 20% 15%, rgba(124,58,237,0.18), transparent 15rem), rgba(2,10,22,0.82)",
        boxShadow: "inset 0 0 70px rgba(95,243,255,0.08)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: { xs: 318, md: 520 },
          overflow: "hidden",
          borderRadius: "14px",
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        >
          <defs>
            <filter id="ndLineGlow">
              <feGaussianBlur stdDeviation="0.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {activeNodes.map((node) => {
            const mobilePosition = mobileNodePositions[node.label] || node;
            return (
              <line
                key={node.label}
                x1="50"
                y1="50"
                x2={mobilePosition.x}
                y2={mobilePosition.y}
                stroke="rgba(95,243,255,0.45)"
                strokeWidth="0.22"
                strokeDasharray="1 1"
                filter="url(#ndLineGlow)"
              />
            );
          })}
        </Box>
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: 132, md: 196 },
            height: { xs: 132, md: 196 },
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            textAlign: "center",
            background:
              "radial-gradient(circle, rgba(95,243,255,0.20), rgba(124,58,237,0.12) 55%, rgba(3,15,30,0.86))",
            border: "1px solid rgba(95,243,255,0.72)",
            boxShadow: "0 0 0 10px rgba(95,243,255,0.05), 0 0 60px rgba(95,243,255,0.26)",
            animation: "ndNodePulse 2.8s ease-in-out infinite",
            px: 1.2,
            zIndex: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                color: "#5ff3ff",
                fontSize: { xs: 10, md: 12 },
                fontWeight: 900,
                letterSpacing: 1,
              }}
            >
              TARGET DOMAIN
            </Typography>
            <Typography sx={{ color: "#fff", fontSize: { xs: 18, md: 24 }, fontWeight: 900 }}>
              {domain}
            </Typography>
          </Box>
        </Box>
        {discoveryNodes.map((node, index) => {
          const active = node.step <= stepIndex;
          const mobilePosition = mobileNodePositions[node.label] || node;
          const left = { xs: `${mobilePosition.x}%`, md: `${node.x}%` };
          const top = { xs: `${mobilePosition.y}%`, md: `${node.y}%` };
          return (
            <Box
              key={node.label}
              sx={{
                position: "absolute",
                left,
                top,
                transform: "translate(-50%, -50%)",
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.45, md: 0.8 },
                opacity: active ? 1 : 0,
                transition: "opacity 320ms ease, transform 320ms ease",
                zIndex: 3,
                pointerEvents: "none",
                maxWidth: { xs: 132, md: "none" },
              }}
            >
              <Box
                sx={{
                  width: { xs: 11, md: 14 },
                  height: { xs: 11, md: 14 },
                  borderRadius: "50%",
                  border: "2px solid #5ff3ff",
                  background: "rgba(3,15,30,0.92)",
                  boxShadow: "0 0 20px rgba(95,243,255,0.7)",
                  animation: active ? "ndNodePop 0.55s ease both" : "none",
                  animationDelay: `${(index % 3) * 80}ms`,
                  flex: "0 0 auto",
                }}
              />
              <Typography
                sx={{
                  color: node.label === "Generating Risks..." ? "#ff7a18" : "#d8e7ff",
                  fontSize: { xs: 10.5, md: 13 },
                  fontWeight: 900,
                  px: { xs: 0.7, md: 1 },
                  py: { xs: 0.3, md: 0.45 },
                  borderRadius: "999px",
                  background: "rgba(3,15,30,0.78)",
                  border: "1px solid rgba(95,243,255,0.18)",
                  whiteSpace: "normal",
                  lineHeight: 1.15,
                  textShadow: "0 0 14px rgba(0,0,0,0.8)",
                }}
              >
                {node.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box
        sx={{
          mx: { xs: 1.5, md: 0 },
          mb: { xs: 1.5, md: 0 },
          position: { xs: "relative", md: "absolute" },
          left: { md: 24 },
          bottom: { md: 22 },
          right: { md: "auto" },
          maxWidth: { md: 420 },
          p: { xs: 1.4, md: 1.8 },
          borderRadius: "10px",
          border: "1px solid rgba(95,243,255,0.22)",
          background: "rgba(2,10,22,0.72)",
        }}
      >
        <Typography sx={{ color: "#5ff3ff", fontSize: 12, fontWeight: 900 }}>
          LIVE DISCOVERY STATUS
        </Typography>
        <Typography sx={{ color: "#fff", fontWeight: 900, mt: 0.6, fontSize: { xs: 22, md: 24 } }}>
          {status}
        </Typography>
      </Box>
    </Box>
  );
}

function ScanningState({ stepIndex, onFastForward, navigate, domain }) {
  const progress = Math.min(100, ((stepIndex + 1) / scanSteps.length) * 100);
  return (
    <Box sx={{ display: "grid", gap: 2.2 }}>
      <TopBar
        title="Discovery in progress"
        subtitle="Fast-forwarding a realistic exposure discovery workflow."
        navigate={navigate}
        mobileHidden
      />
      <Box sx={{ ...panel, p: { xs: 2.2, md: 4 } }}>
        <Typography sx={{ color: "#fff", fontSize: { xs: 30, md: 42 }, fontWeight: 900 }}>
          Finding exposed assets and business risk
        </Typography>
        <Typography sx={{ color: "rgba(248,251,255,0.74)", mt: 1, mb: 3, maxWidth: 780 }}>
          NetDefenders is mapping public-facing signals, exposed assets, breach indicators and risk
          context for this organisation.
        </Typography>
        <DiscoveryGraph domain={getDomainLabel(domain)} stepIndex={stepIndex} />
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 12,
            mt: 3,
            borderRadius: 999,
            background: "rgba(148,163,184,0.18)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              background: "linear-gradient(135deg, #5ff3ff, #7c3aed)",
            },
          }}
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(5, 1fr)" },
            gap: 1.2,
            mt: 3,
          }}
        >
          {scanSteps.map((item, index) => {
            const active = index === stepIndex;
            const done = index < stepIndex;
            return (
              <Box
                key={item}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  color: done || active ? "#fff" : "rgba(248,251,255,0.42)",
                  fontWeight: active ? 900 : 700,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    border: `1px solid ${done || active ? "#5ff3ff" : "rgba(148,163,184,0.24)"}`,
                    color: done || active ? "#5ff3ff" : "rgba(248,251,255,0.42)",
                    flex: "0 0 auto",
                  }}
                >
                  {done ? "✓" : index + 1}
                </Box>
                <Typography sx={{ fontWeight: "inherit", fontSize: 14 }}>{item}</Typography>
              </Box>
            );
          })}
        </Box>
        <Button sx={{ ...outline, mt: 3 }} onClick={onFastForward}>
          Fast-forward demo
        </Button>
      </Box>
    </Box>
  );
}

function TopFinding({ finding, index, onOpen, onFix, tourTarget }) {
  const icons = [LockOutlinedIcon, MailOutlineIcon, PublicOutlinedIcon];
  const Icon = icons[index] || WarningAmberOutlinedIcon;
  const accent = severityColor(finding.severity);
  const highlighted = tourTarget === "first-finding" && index === 0;
  const tourActive = Boolean(tourTarget);
  const canOpen = !tourActive || (tourTarget === "view-details" && index === 0);
  const canFix = !tourActive || (tourTarget === "fix" && index === 0);
  return (
    <Box
      data-demo-target={index === 0 ? "tour-first-finding" : undefined}
      sx={{
        ...panel,
        position: "relative",
        minHeight: { md: 186 },
        p: { xs: 2, lg: "28px 28px" },
        borderColor: highlighted ? "#5ff3ff" : `${accent}cc`,
        boxShadow: highlighted
          ? "0 0 0 4px rgba(95,243,255,0.16), 0 20px 70px rgba(0,0,0,0.28)"
          : panel.boxShadow,
        background:
          "linear-gradient(135deg, rgba(15,25,45,0.98), rgba(3,15,30,0.96)), radial-gradient(circle at 4% 18%, rgba(255,255,255,0.06), transparent 16rem)",
        boxSizing: "border-box",
        [compactDesktop]: { height: "clamp(118px, 14vh, 142px)", minHeight: 0, p: "12px 20px" },
      }}
    >
      <HighlightDot active={highlighted} sx={{ left: 26, top: 26 }} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "48px 1fr", md: "54px 110px minmax(330px,1fr) 148px 252px" },
          alignItems: "center",
          gap: { xs: 1.5, md: 2.6 },
          [compactDesktop]: {
            height: "100%",
            gridTemplateColumns: "44px 76px minmax(300px,1fr) 124px 224px",
            gap: 1.8,
          },
        }}
      >
        <Box>
          <Box
            sx={{
              width: { xs: 44, md: 48 },
              height: { xs: 44, md: 48 },
              borderRadius: "9px",
              display: "grid",
              placeItems: "center",
              border: `1px solid ${accent}`,
              background: `linear-gradient(135deg, ${accent}55, ${accent}22)`,
              fontWeight: 900,
              fontSize: 24,
              color: "#fff",
              boxShadow: `0 0 28px ${accent}35`,
              [compactDesktop]: { width: 38, height: 38, fontSize: 20 },
            }}
          >
            {index + 1}
          </Box>
        </Box>
        <Box sx={{ gridColumn: { xs: "2", md: "auto" } }}>
          <Box
            sx={{
              width: { xs: 68, md: 88 },
              height: { xs: 68, md: 88 },
              borderRadius: "10px",
              display: "grid",
              placeItems: "center",
              color: accent,
              border: `1px solid ${accent}aa`,
              background: `linear-gradient(135deg, ${accent}21, rgba(255,255,255,0.03))`,
              boxShadow: `inset 0 0 26px ${accent}16`,
              [compactDesktop]: { width: 62, height: 62 },
            }}
          >
            <Icon sx={{ fontSize: { xs: 34, md: 43 }, [compactDesktop]: { fontSize: 31 } }} />
          </Box>
        </Box>
        <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
          <Typography
            sx={{
              color: "#fff",
              fontSize: { xs: 19, md: 22 },
              fontWeight: 900,
              lineHeight: 1.2,
              [compactDesktop]: { fontSize: 18, lineHeight: 1.18 },
            }}
          >
            {finding.title}
          </Typography>
          <Typography
            sx={{
              color: "rgba(248,251,255,0.76)",
              mt: 0.7,
              fontSize: 16,
              lineHeight: 1.45,
              maxWidth: 425,
              [compactDesktop]: {
                fontSize: 13.6,
                lineHeight: 1.3,
                mt: 0.35,
                maxHeight: 38,
                overflow: "hidden",
              },
            }}
          >
            {finding.shortDescription}
          </Typography>
        </Box>
        <Box
          sx={{
            gridColumn: { xs: "1 / -1", md: "auto" },
            borderLeft: { md: "1px solid rgba(148,163,184,0.18)" },
            height: { md: 128 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            pl: { md: 2.6 },
            [compactDesktop]: { height: 92, pl: 1.8 },
          }}
        >
          <Typography sx={{ color: "rgba(248,251,255,0.68)", fontSize: 12, fontWeight: 800 }}>
            SEVERITY
          </Typography>
          <Box sx={{ mt: 0.8, [compactDesktop]: { mt: 0.45 } }}>
            <SeverityBadge severity={finding.severity} />
          </Box>
          <Typography
            sx={{
              color: accent,
              fontSize: { xs: 30, md: 36 },
              fontWeight: 900,
              mt: 1,
              lineHeight: 1,
              [compactDesktop]: { fontSize: 24, mt: 0.45 },
            }}
          >
            {String(finding.score).replace("/10", "")}
            <Typography component="span" sx={{ color: "#a8b3c7", fontSize: 17, fontWeight: 700 }}>
              /10
            </Typography>
          </Typography>
        </Box>
        <Box
          sx={{
            gridColumn: { xs: "1 / -1", md: "auto" },
            borderLeft: { md: "1px solid rgba(148,163,184,0.18)" },
            height: { md: 128 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            pl: { md: 2.6 },
            [compactDesktop]: { height: 92, pl: 1.8 },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr" },
              gap: 1.2,
              [compactDesktop]: { gap: 0.55 },
            }}
          >
            <Box
              data-demo-target={index === 0 ? "tour-view-details" : undefined}
              sx={{ position: "relative" }}
            >
              <Button
                startIcon={<VisibilityOutlinedIcon />}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  ...outline,
                  minHeight: 52,
                  width: "100%",
                  justifyContent: "space-between",
                  px: 2,
                  borderColor:
                    tourTarget === "view-details" && index === 0 ? "#5ff3ff" : outline.border,
                  boxShadow:
                    tourTarget === "view-details" && index === 0
                      ? "0 0 0 4px rgba(95,243,255,0.12)"
                      : "none",
                  [compactDesktop]: { minHeight: 36, fontSize: 13 },
                }}
                disabled={!canOpen}
                onClick={() => canOpen && onOpen(finding.id)}
              >
                View details
              </Button>
              <HighlightDot
                active={tourTarget === "view-details" && index === 0}
                label="Click here"
                sx={{ right: 0, top: "50%", transform: "translate(50%, -50%)" }}
              />
            </Box>
            <Box
              data-demo-target={index === 0 ? "tour-fix-button" : undefined}
              sx={{ position: "relative" }}
            >
              <Button
                startIcon={<BuildOutlinedIcon />}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  ...primary,
                  minHeight: 52,
                  width: "100%",
                  justifyContent: "space-between",
                  px: 2,
                  borderColor: tourTarget === "fix" && index === 0 ? "#5ff3ff" : primary.border,
                  boxShadow:
                    tourTarget === "fix" && index === 0
                      ? "0 0 0 4px rgba(95,243,255,0.12)"
                      : primary.boxShadow,
                  [compactDesktop]: { minHeight: 36, fontSize: 13 },
                }}
                disabled={!canFix}
                onClick={() => canFix && onFix(finding.id)}
              >
                See how to fix
              </Button>
              <HighlightDot
                active={tourTarget === "fix" && index === 0}
                label="Click here"
                sx={{ right: 0, top: "50%", transform: "translate(50%, -50%)" }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function ResultsBanner({ onTour }) {
  return (
    <Box
      sx={{
        ...panel,
        p: { xs: 2.2, md: 2.6 },
        mb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
        borderColor: "rgba(95,243,255,0.45)",
      }}
    >
      <Box>
        <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: 22 }}>
          Discovery complete.
        </Typography>
        <Typography sx={{ color: "rgba(248,251,255,0.74)", mt: 0.4 }}>
          We found the issues most likely to create business impact.
        </Typography>
      </Box>
      <Box
        data-demo-target="show-matters-first"
        sx={{ position: "relative", display: "inline-flex" }}
      >
        <Button
          sx={{
            ...primary,
            animation: "ndCtaPulse 1.45s ease-in-out infinite",
            boxShadow: "0 0 0 0 rgba(95,243,255,0.24), inset 0 1px 0 rgba(255,255,255,0.16)",
          }}
          onClick={onTour}
          endIcon={<ArrowForwardIcon />}
        >
          Show me what matters first
        </Button>
        <HighlightDot
          active
          label="Next"
          sx={{ right: -4, top: -4, transform: "translate(50%, -50%)" }}
        />
      </Box>
    </Box>
  );
}

function OverviewResults({ goRisk, navigate, stage, onTour, tourTarget, onTourFix }) {
  const additionalIssues = Math.max(demoFindings.length - 3, 0);
  return (
    <>
      <TopBar
        title="Critical Vulnerabilities Requiring Action"
        subtitle="These issues are most likely to be used by attackers right now."
        navigate={navigate}
        tourTarget={tourTarget}
        mobileHidden
      />
      {stage === stages.RESULTS && <ResultsBanner onTour={onTour} />}
      <Box
        data-demo-target="tour-score"
        sx={{
          ...panel,
          position: "relative",
          minHeight: { md: 191 },
          p: { xs: 2.4, md: 2.8 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1.05fr 0.8fr 1.15fr" },
          alignItems: "stretch",
          gap: 1.4,
          mb: 1.7,
          borderColor: tourTarget === "score" ? "#5ff3ff" : "rgba(139,92,246,0.5)",
          boxShadow:
            tourTarget === "score"
              ? "0 0 0 4px rgba(95,243,255,0.16), 0 20px 70px rgba(0,0,0,0.28)"
              : panel.boxShadow,
          boxSizing: "border-box",
          [compactDesktop]: {
            height: "clamp(116px, 14vh, 142px)",
            minHeight: 0,
            p: "12px 18px",
            gridTemplateColumns: "1fr 1.05fr 0.8fr 1.15fr",
            gap: 1,
            mb: 0.9,
          },
        }}
      >
        <HighlightDot
          active={tourTarget === "score"}
          sx={{ left: { xs: 28, md: 96 }, top: { xs: 72, md: 92 } }}
        />
        <Box sx={{ borderRight: { md: "1px solid rgba(148,163,184,0.18)" }, pr: { md: 1.7 } }}>
          <Typography sx={{ color: "rgba(248,251,255,0.72)", fontSize: 13, fontWeight: 900 }}>
            TOTAL EXPOSURE SCORE
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 2, md: 3.2 },
              mt: 0.4,
              flexWrap: "wrap",
              [compactDesktop]: { gap: 2, mt: 0.15 },
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontSize: { xs: 76, md: 100 },
                fontWeight: 900,
                lineHeight: 0.86,
                [compactDesktop]: { fontSize: 58 },
              }}
            >
              82
              <Typography component="span" sx={{ color: "#91a0b7", fontSize: 34 }}>
                /100
              </Typography>
            </Typography>
            <Chip
              label="HIGH RISK"
              sx={{
                color: "#ff7a18",
                border: "1px solid #ff7a18",
                background: "rgba(255,122,24,0.1)",
                fontWeight: 900,
                height: 38,
                borderRadius: "8px",
                [compactDesktop]: { height: 30, fontSize: 12 },
              }}
            />
          </Box>
        </Box>
        <Box sx={{ borderRight: { md: "1px solid rgba(148,163,184,0.18)" }, pr: { md: 1.7 } }}>
          <Typography sx={{ color: "rgba(248,251,255,0.72)", fontSize: 13, fontWeight: 900 }}>
            EXPOSURE SUMMARY
          </Typography>
          <Typography
            sx={{
              color: "#ff7a18",
              fontSize: 24,
              fontWeight: 900,
              [compactDesktop]: { fontSize: 20 },
            }}
          >
            Your exposure is high
          </Typography>
          <Typography
            sx={{
              color: "rgba(248,251,255,0.78)",
              mt: 1.3,
              lineHeight: 1.55,
              maxWidth: 360,
              [compactDesktop]: { fontSize: 14.2, lineHeight: 1.32, mt: 0.45 },
            }}
          >
            Attackers could exploit these issues to access your systems, steal data, or disrupt your
            operations.
          </Typography>
        </Box>
        <Box sx={{ borderRight: { md: "1px solid rgba(148,163,184,0.18)" }, pr: { md: 1.7 } }}>
          <Typography sx={{ color: "rgba(248,251,255,0.72)", fontSize: 13, fontWeight: 900 }}>
            LAST SCAN DATE
          </Typography>
          <Typography sx={{ color: "#fff", fontSize: { xs: 26, md: 30 }, fontWeight: 900, mt: 1 }}>
            Today
          </Typography>
          <Typography sx={{ color: "rgba(248,251,255,0.72)", mt: 0.7, lineHeight: 1.45 }}>
            Initial external discovery scan completed.
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ color: "rgba(248,251,255,0.72)", fontSize: 13, fontWeight: 900 }}>
            VULNERABILITY TREND
          </Typography>
          <MiniTrendGraph data={demoTrend} metric="vulnerabilities" color="#ff7a18" height={92} />
          <Typography sx={{ color: "rgba(248,251,255,0.72)", fontSize: 13, mt: 0.5 }}>
            {demoTrend[0].label} to {demoTrend[demoTrend.length - 1].label}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "grid", gap: 1.55, [compactDesktop]: { gap: 0.7 } }}>
        {demoFindings.slice(0, 3).map((finding, index) => (
          <TopFinding
            key={finding.id}
            finding={finding}
            index={index}
            onOpen={(id) => goRisk(id)}
            onFix={(id) => {
              goRisk(id, "fix");
              if (tourTarget === "fix" && index === 0) onTourFix?.();
            }}
            tourTarget={tourTarget}
          />
        ))}
      </Box>
      <Box
        sx={{
          ...panel,
          minHeight: { md: 119 },
          p: { xs: 2.5, md: "20px 28px 20px 24px" },
          mt: 1.55,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          [compactDesktop]: { minHeight: 58, p: "8px 20px", mt: 0.7, gap: 1 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              color: "#fff",
              background: "linear-gradient(135deg, #7c3aed, #4c1d95)",
              border: "1px solid rgba(167,139,250,0.7)",
              [compactDesktop]: { width: 40, height: 40 },
            }}
          >
            <ShieldOutlinedIcon />
          </Box>
          <Box>
            <Typography
              sx={{
                color: "#fff",
                fontSize: 24,
                fontWeight: 900,
                [compactDesktop]: { fontSize: 17 },
              }}
            >
              Do not leave these issues unaddressed.
            </Typography>
            <Typography
              sx={{
                color: "rgba(248,251,255,0.74)",
                mt: 0.5,
                [compactDesktop]: { fontSize: 12.8 },
              }}
            >
              Fixing now significantly reduces your risk of a breach or downtime.
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            textAlign: { xs: "left", sm: "right" },
            [compactDesktop]: { display: "grid", justifyItems: "end", gap: 0.4 },
          }}
        >
          <Button
            endIcon={<ArrowForwardIcon />}
            sx={{
              ...primary,
              minWidth: 230,
              [compactDesktop]: { minHeight: 34, minWidth: 198, fontSize: 13 },
            }}
            onClick={() => goRisk()}
          >
            View all vulnerabilities
          </Button>
          <Typography
            sx={{
              color: "#ff7a18",
              mt: 1,
              fontWeight: 900,
              [compactDesktop]: { mt: 0, fontSize: 13 },
            }}
          >
            {additionalIssues} additional {additionalIssues === 1 ? "issue" : "issues"} detected
          </Typography>
        </Box>
      </Box>
    </>
  );
}

function Overview(props) {
  if (props.stage === stages.EMPTY)
    return <EmptyState onStart={props.onStart} navigate={props.navigate} />;
  if (props.stage === stages.FORM) return <SetupForm {...props} />;
  if (props.stage === stages.SCANNING)
    return (
      <ScanningState
        stepIndex={props.stepIndex}
        onFastForward={props.onFastForward}
        navigate={props.navigate}
        domain={props.form?.domains}
      />
    );
  return <OverviewResults {...props} />;
}

function Info({ title, children, highlight, showDot, target }) {
  return (
    <Box
      data-demo-target={target}
      sx={{
        ...panel,
        p: { xs: 2.5, md: 3 },
        mb: 2,
        position: "relative",
        borderColor: highlight ? "#5ff3ff" : "rgba(74,104,143,0.44)",
      }}
    >
      <HighlightDot active={showDot} sx={{ right: 16, top: 20 }} />
      <Typography sx={{ color: "#fff", fontSize: 24, fontWeight: 900 }}>{title}</Typography>
      {String(children)
        .split("\n")
        .map((line) => (
          <Typography key={line} sx={{ color: "rgba(248,251,255,0.8)", mt: 1.2, lineHeight: 1.65 }}>
            {line}
          </Typography>
        ))}
    </Box>
  );
}

function FindingDetail({ finding, focusFix, back, tourTarget }) {
  if (!finding) return null;
  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Button sx={{ ...outline, width: "fit-content" }} onClick={back}>
        Back to findings table
      </Button>
      <Box sx={{ ...panel, p: { xs: 2.5, md: 3 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Chip
              label={finding.status}
              sx={{
                color: "#a78bfa",
                border: "1px solid #a78bfa",
                background: "rgba(139,92,246,0.14)",
                fontWeight: 900,
              }}
            />
            <Typography
              sx={{ color: "#fff", fontSize: { xs: 28, md: 38 }, fontWeight: 900, mt: 2 }}
            >
              {finding.title}
            </Typography>
            <Typography sx={{ color: "rgba(248,251,255,0.72)", mt: 1 }}>
              {finding.category} - {finding.affectedAsset}
            </Typography>
          </Box>
          <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
            <SeverityBadge severity={finding.severity} />
            <Typography
              sx={{ color: severityColor(finding.severity), fontSize: 42, fontWeight: 900, mt: 1 }}
            >
              {finding.score}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Info title="Narrative">{finding.narrative}</Info>
          <Info
            title="Tailored remediation steps"
            highlight={focusFix || tourTarget === "remediation"}
            showDot={tourTarget === "remediation"}
            target="tour-remediation"
          >
            {finding.remediation.map((item) => `• ${item}`).join("\n")}
          </Info>
          <Info title="Evidence">{finding.evidence.map((item) => `• ${item}`).join("\n")}</Info>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Info title="Downstream impacts">
            {finding.downstreamImpacts.map((item) => `• ${item}`).join("\n")}
          </Info>
          <Info title="Business impact">{finding.businessImpact}</Info>
        </Grid>
      </Grid>
    </Box>
  );
}

function Risks({ selectedId, focusFix, select, back, tourTarget }) {
  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState("all");
  const [category, setCategory] = useState("all");
  const selected = demoFindings.find((finding) => finding.id === selectedId);
  const categories = Array.from(new Set(demoFindings.map((finding) => finding.category)));
  const filtered = demoFindings.filter(
    (finding) =>
      [finding.title, finding.category, finding.host, finding.severity]
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase()) &&
      (severity === "all" || finding.severity === severity) &&
      (category === "all" || finding.category === category)
  );
  if (selected)
    return (
      <>
        <Heading
          title="Risks"
          subtitle="Narrative first, evidence second, fixes clearly listed for action."
        />
        <FindingDetail finding={selected} focusFix={focusFix} back={back} tourTarget={tourTarget} />
      </>
    );
  return (
    <>
      <Heading
        title="Risks"
        subtitle="Select a finding to view what it means, why it matters, and how to fix it."
      />
      <Grid container spacing={1.5} sx={{ mb: 2, alignItems: "stretch" }}>
        <Grid item xs={12} md={4.4}>
          <TextField
            fullWidth
            size="small"
            label="Search findings"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={12} md={2.4}>
          <TextField
            select
            fullWidth
            size="small"
            label="Severity"
            value={severity}
            onChange={(event) => setSeverity(event.target.value)}
            sx={fieldSx}
          >
            {["all", "Critical", "High", "Medium", "Low"].map((item) => (
              <MenuItem key={item} value={item}>
                {item === "all" ? "All severities" : item}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            select
            fullWidth
            size="small"
            label="Category"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            sx={fieldSx}
          >
            <MenuItem value="all">All categories</MenuItem>
            {categories.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={2.2}>
          <Button
            fullWidth
            startIcon={<FileDownloadOutlinedIcon />}
            sx={{ ...outline, minHeight: 44 }}
            onClick={() => exportFindingsCsv(filtered)}
          >
            Export CSV
          </Button>
        </Grid>
      </Grid>
      <Box
        data-demo-target="tour-risks-list"
        sx={{
          overflowX: "auto",
          borderRadius: "8px",
          border:
            tourTarget === "risks-list" ? "2px solid #5ff3ff" : "1px solid rgba(148,163,184,0.22)",
          position: "relative",
          boxShadow: tourTarget === "risks-list" ? "0 0 0 4px rgba(95,243,255,0.12)" : "none",
        }}
      >
        <HighlightDot active={tourTarget === "risks-list"} sx={{ right: 18, top: 28 }} />
        <Box sx={{ minWidth: 1130 }}>
          <Box
            sx={{
              ...tableHeaderSx,
              gridTemplateColumns: riskTableColumns,
            }}
          >
            {["Score", "Vulnerability", "Category", "Host", "Severity"].map((heading) => (
              <Box key={heading} sx={riskCell}>
                {heading}
              </Box>
            ))}
          </Box>
          {filtered.map((finding) => (
            <FindingTableRow key={finding.id} finding={finding} select={select} />
          ))}
        </Box>
      </Box>
    </>
  );
}

function FindingTableRow({ finding, select }) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => select(finding.id)}
      onKeyDown={(event) => event.key === "Enter" && select(finding.id)}
      sx={{
        ...tableRowSx,
        gridTemplateColumns: riskTableColumns,
        cursor: "pointer",
        "&:hover": { background: "rgba(124,58,237,0.16)" },
      }}
    >
      <Box sx={riskCell}>
        <Typography sx={{ color: severityColor(finding.severity), fontWeight: 900, fontSize: 24 }}>
          {finding.numericScore}
        </Typography>
        <Typography sx={{ color: "#9fb4d6", fontSize: 12 }}>{finding.severity}</Typography>
      </Box>
      <Box sx={riskCell}>
        <Typography sx={{ color: "#5ff3ff", fontWeight: 900, fontSize: 18 }}>
          {finding.title}
        </Typography>
      </Box>
      <Box sx={{ ...riskCell, fontSize: 17 }}>{finding.category}</Box>
      <Box sx={{ ...riskCell, color: "#5ff3ff", fontWeight: 900, fontSize: 18 }}>
        {finding.host}
      </Box>
      <Box sx={riskCell}>
        <SeverityBadge severity={finding.severity} />
      </Box>
    </Box>
  );
}

function Heading({ title, subtitle }) {
  return (
    <>
      <Typography sx={{ color: "#fff", fontSize: { xs: 31, md: 40 }, fontWeight: 900 }}>
        {title}
      </Typography>
      <Typography sx={{ color: "rgba(248,251,255,0.74)", fontSize: 18, mt: 1, mb: 3 }}>
        {subtitle}
      </Typography>
    </>
  );
}

function ListPage({ title, subtitle, rows, render }) {
  return (
    <>
      <Heading title={title} subtitle={subtitle} />
      <Box sx={{ display: "grid", gap: 1.4 }}>
        {rows.map((row) => (
          <Box key={typeof row === "string" ? row : row.id} sx={{ ...panel, p: 2.5 }}>
            {render(row)}
          </Box>
        ))}
      </Box>
    </>
  );
}

function MonitoringPage({ goRisks }) {
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState(["All assets"]);
  const [frequency, setFrequency] = useState("Weekly");
  const [day, setDay] = useState("Monday");
  const [time, setTime] = useState("09:00");
  const [schedules, setSchedules] = useState([
    {
      id: "schedule-all-assets",
      group: "All assets",
      cadence: "Weekly",
      scanDateTime: "Mondays at 09:00",
      type: "Scheduled scan",
    },
  ]);
  const historyRows = [
    {
      id: "scan-demo-initial",
      group: "All assets",
      scanDateTime: "2026-05-27 09:00",
      type: "Manually executed",
    },
  ];
  const assetOptions = ["All assets", ...demoAssets.map((asset) => asset.domainName)];
  const toggleAsset = (asset) => {
    setSelectedAssets((current) =>
      current.includes(asset) ? current.filter((item) => item !== asset) : [...current, asset]
    );
  };
  const createSchedule = () => {
    const group = selectedAssets.length ? selectedAssets.join(", ") : "All assets";
    setSchedules((current) => [
      ...current,
      {
        id: `schedule-${Date.now()}`,
        group,
        cadence: frequency,
        scanDateTime: `${day}s at ${time}`,
        type: "Scheduled scan",
      },
    ]);
    setScheduleOpen(false);
  };
  const scheduleColumns = [
    {
      header: "Asset Group",
      width: "1.5fr",
      render: (row) => <Typography sx={{ color: "#fff", fontWeight: 900 }}>{row.group}</Typography>,
    },
    {
      header: "Scan Date / Time",
      width: "1fr",
      render: (row) => (
        <Typography sx={{ color: "#5ff3ff", fontWeight: 900 }}>{row.scanDateTime}</Typography>
      ),
    },
    {
      header: "Scan Type",
      width: "1fr",
      render: (row) => <Typography sx={{ color: "rgba(248,251,255,0.82)" }}>{row.type}</Typography>,
    },
  ];
  return (
    <>
      <Heading
        title="Monitoring"
        subtitle="Continuous monitoring keeps your external exposure under review after the first discovery scan."
      />
      <Box
        sx={{
          ...panel,
          p: { xs: 2.4, md: 3 },
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography sx={{ color: "#fff", fontSize: 24, fontWeight: 900 }}>
            Create a new scheduled scan
          </Typography>
          <Typography sx={{ color: "rgba(248,251,255,0.76)", mt: 0.7 }}>
            Choose the assets and when NetDefenders should run the scan.
          </Typography>
        </Box>
        <Button sx={primary} onClick={() => setScheduleOpen(true)}>
          New scheduled scan
        </Button>
      </Box>
      <Box sx={{ display: "grid", gap: 2 }}>
        <Box>
          <Typography sx={{ color: "#fff", fontSize: 24, fontWeight: 900, mb: 1.5 }}>
            Scheduled scans
          </Typography>
          <DataTable columns={scheduleColumns} rows={schedules} minWidth={840} />
        </Box>
        <Box>
          <Typography sx={{ color: "#fff", fontSize: 24, fontWeight: 900, mb: 1.5 }}>
            Scan history
          </Typography>
          <Box
            role="button"
            tabIndex={0}
            onClick={goRisks}
            onKeyDown={(event) => event.key === "Enter" && goRisks()}
            sx={{ cursor: "pointer", "&:hover": { opacity: 0.9 } }}
          >
            <DataTable columns={scheduleColumns} rows={historyRows} minWidth={840} />
          </Box>
        </Box>
      </Box>
      {scheduleOpen && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1500,
            display: "grid",
            placeItems: "center",
            background: "rgba(1,6,14,0.62)",
            p: 2,
          }}
        >
          <Box sx={{ ...panel, p: { xs: 2.4, md: 3 }, width: "min(680px, 100%)" }}>
            <Typography sx={{ color: "#fff", fontSize: 28, fontWeight: 900 }}>
              New scheduled scan
            </Typography>
            <Typography sx={{ color: "rgba(248,251,255,0.72)", mt: 0.7, mb: 2 }}>
              Select the asset group and scan cadence.
            </Typography>
            <Typography sx={{ color: "#fff", fontWeight: 900, mb: 1 }}>Asset group</Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {assetOptions.map((asset) => (
                <Grid item xs={12} sm={6} key={asset}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedAssets.includes(asset)}
                        onChange={() => toggleAsset(asset)}
                        sx={{
                          color: "rgba(248,251,255,0.64)",
                          "&.Mui-checked": { color: "#5ff3ff" },
                        }}
                      />
                    }
                    label={asset}
                    sx={{ color: "rgba(248,251,255,0.86)" }}
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Frequency"
                  value={frequency}
                  onChange={(event) => setFrequency(event.target.value)}
                  sx={fieldSx}
                >
                  {["Weekly", "Monthly"].map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Day"
                  value={day}
                  onChange={(event) => setDay(event.target.value)}
                  sx={fieldSx}
                >
                  {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Time"
                  value={time}
                  onChange={(event) => setTime(event.target.value)}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.2, mt: 2.5 }}>
              <Button sx={outline} onClick={() => setScheduleOpen(false)}>
                Cancel
              </Button>
              <Button sx={primary} onClick={createSchedule}>
                Create schedule
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}

function AssetsPage() {
  const columns = [
    {
      header: "IP Address",
      width: "1fr",
      render: (asset) => (
        <Typography sx={{ color: "#fff", fontWeight: 900 }}>{asset.ipAddress}</Typography>
      ),
    },
    {
      header: "Domain Name",
      width: "1.35fr",
      render: (asset) => (
        <Typography sx={{ color: "#5ff3ff", fontWeight: 900 }}>{asset.domainName}</Typography>
      ),
    },
    {
      header: "Operating System",
      width: "1.25fr",
      render: (asset) => (
        <Typography sx={{ color: "rgba(248,251,255,0.82)" }}>{asset.operatingSystem}</Typography>
      ),
    },
    {
      header: "Open Ports",
      width: "0.75fr",
      render: (asset) => (
        <Typography sx={{ color: "#fff", fontWeight: 900 }}>{asset.openPorts.length}</Typography>
      ),
    },
    {
      header: "Vulnerabilities",
      width: "0.9fr",
      render: (asset) => (
        <Typography
          sx={{
            color: severityColor(asset.vulnerabilities.length ? "High" : "Low"),
            fontWeight: 900,
          }}
        >
          {asset.vulnerabilities.length}
        </Typography>
      ),
    },
  ];
  return (
    <>
      <Heading title="Assets" subtitle="Known public-facing assets in scope." />
      <DataTable columns={columns} rows={demoAssets} minWidth={960} />
    </>
  );
}

function CredentialsPage({ resolved, setResolved }) {
  const toggleResolved = (id) => setResolved((current) => ({ ...current, [id]: !current[id] }));
  const columns = [
    {
      header: "Username",
      width: "1.15fr",
      render: (credential) => (
        <Typography sx={{ color: "#fff", fontWeight: 900 }}>{credential.username}</Typography>
      ),
    },
    {
      header: "Asset",
      width: "1.25fr",
      render: (credential) => (
        <Typography sx={{ color: "#5ff3ff", fontWeight: 900 }}>{credential.asset}</Typography>
      ),
    },
    {
      header: "Breach Source",
      width: "1.15fr",
      render: (credential) => (
        <Typography sx={{ color: "rgba(248,251,255,0.82)" }}>{credential.breachSource}</Typography>
      ),
    },
    {
      header: "Date Discovered",
      width: "0.9fr",
      render: (credential) => (
        <Typography sx={{ color: "rgba(248,251,255,0.82)", fontWeight: 800 }}>
          {credential.discoveredAt}
        </Typography>
      ),
    },
    {
      header: "Resolved",
      width: "0.75fr",
      render: (credential) => (
        <Checkbox
          checked={Boolean(resolved[credential.id])}
          onChange={() => toggleResolved(credential.id)}
          sx={{
            color: "rgba(248,251,255,0.64)",
            "&.Mui-checked": { color: "#5ff3ff" },
          }}
        />
      ),
    },
  ];
  return (
    <>
      <Heading
        title="Credentials"
        subtitle="Known credential exposure that may create account takeover risk."
      />
      <DataTable
        columns={columns}
        rows={demoCredentials}
        minWidth={980}
        rowSx={(credential) =>
          resolved[credential.id]
            ? {
                opacity: 0.42,
                filter: "grayscale(1)",
                background: "rgba(8,23,43,0.42)",
              }
            : {}
        }
      />
    </>
  );
}

function ReportMetric({ title, metric, color }) {
  return (
    <Box sx={{ ...panel, p: { xs: 2.4, md: 3 } }}>
      <Typography sx={{ color: "#fff", fontSize: 22, fontWeight: 900 }}>{title}</Typography>
      <MiniTrendGraph data={demoTrend} metric={metric} color={color} height={230} showYAxis />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          color: "rgba(248,251,255,0.68)",
          mt: 1,
        }}
      >
        {demoTrend.map((item) => (
          <Typography key={`${metric}-${item.label}`} sx={{ fontSize: 12, fontWeight: 800 }}>
            {item.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

function ReportsPage() {
  return (
    <>
      <Heading title="Reports" subtitle="Trend summaries for leadership and remediation teams." />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ReportMetric
            title="Vulnerabilities over time"
            metric="vulnerabilities"
            color="#ff7a18"
          />
        </Grid>
        <Grid item xs={12}>
          <ReportMetric title="Assets over time" metric="assets" color="#5ff3ff" />
        </Grid>
        <Grid item xs={12}>
          <ReportMetric title="Credentials over time" metric="credentials" color="#a78bfa" />
        </Grid>
      </Grid>
    </>
  );
}

function SettingsPage({ onReset, navigate }) {
  const [users, setUsers] = useState([
    { id: "user-owner", name: "Demo Owner", email: "owner@example.com", role: "Owner" },
    { id: "user-it", name: "IT Contact", email: "it@example.com", role: "Member" },
  ]);
  const [memberEmail, setMemberEmail] = useState("");
  const [passwordForm, setPasswordForm] = useState({ current: "", next: "", confirm: "" });
  const addMember = () => {
    const email = memberEmail.trim();
    if (!email) return;
    setUsers((current) => [
      ...current,
      {
        id: `user-${Date.now()}`,
        name: email.split("@")[0],
        email,
        role: "Member",
      },
    ]);
    setMemberEmail("");
  };
  return (
    <>
      <Heading title="Settings" subtitle="Manage organisation users and account access." />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} lg={7}>
          <Box sx={{ ...panel, p: { xs: 2.4, md: 3 } }}>
            <Typography sx={{ color: "#fff", fontSize: 24, fontWeight: 900, mb: 2 }}>
              Organisation users
            </Typography>
            <DataTable
              minWidth={640}
              rows={users}
              columns={[
                {
                  header: "Name",
                  width: "1fr",
                  render: (user) => (
                    <Typography sx={{ color: "#fff", fontWeight: 900 }}>{user.name}</Typography>
                  ),
                },
                {
                  header: "Email",
                  width: "1.4fr",
                  render: (user) => (
                    <Typography sx={{ color: "#5ff3ff", fontWeight: 900 }}>{user.email}</Typography>
                  ),
                },
                {
                  header: "Role",
                  width: "0.8fr",
                  render: (user) => (
                    <Typography sx={{ color: "rgba(248,251,255,0.82)" }}>{user.role}</Typography>
                  ),
                },
              ]}
            />
            <Box sx={{ display: "flex", gap: 1.2, mt: 2, flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="Member email"
                value={memberEmail}
                onChange={(event) => setMemberEmail(event.target.value)}
                sx={{ ...fieldSx, flex: "1 1 260px" }}
              />
              <Button sx={{ ...primary, minHeight: 44 }} onClick={addMember}>
                Add member
              </Button>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Box sx={{ ...panel, p: { xs: 2.4, md: 3 } }}>
            <Typography sx={{ color: "#fff", fontSize: 24, fontWeight: 900, mb: 2 }}>
              Change password
            </Typography>
            {[
              ["current", "Current password"],
              ["next", "New password"],
              ["confirm", "Confirm new password"],
            ].map(([key, label]) => (
              <TextField
                key={key}
                fullWidth
                type="password"
                size="small"
                label={label}
                value={passwordForm[key]}
                onChange={(event) =>
                  setPasswordForm((current) => ({ ...current, [key]: event.target.value }))
                }
                sx={{ ...fieldSx, mb: 1.4 }}
              />
            ))}
            <Button sx={{ ...primary, minHeight: 44, width: "100%" }}>Update password</Button>
          </Box>
        </Grid>
      </Grid>
      <Box
        sx={{
          ...panel,
          p: 3,
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography sx={{ color: "#fff", fontSize: 24, fontWeight: 900 }}>
            Ready to test your own company?
          </Typography>
          <Typography sx={{ color: "rgba(248,251,255,0.74)", mt: 0.8 }}>
            This demo is front-end only. A free trial starts the real onboarding journey.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button sx={outline} onClick={onReset}>
            Reset demo
          </Button>
          <CtaButton navigate={navigate} />
        </Box>
      </Box>
    </>
  );
}

function TourOverlay({ step, onBack, onNext, onSkip, onFinish, navigate, isSmallMobile }) {
  const details = tourSteps[step];
  const last = step === tourSteps.length - 1;
  const actionStep = details.target === "fix";
  const mobileTourButton = isSmallMobile
    ? { minHeight: 38, minWidth: "auto", px: 1.2, fontSize: 13, whiteSpace: "nowrap" }
    : {};
  return (
    <GuideCard
      title={details.title}
      stepLabel={`Step ${step + 1} of ${tourSteps.length}`}
      placement={details.placement}
      isSmallMobile={isSmallMobile}
    >
      <Typography
        sx={isSmallMobile ? { ...guideText, fontSize: 15, lineHeight: 1.45, mt: 0.8 } : guideText}
      >
        {details.text}
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: isSmallMobile ? "flex-start" : "space-between",
          gap: isSmallMobile ? 0.8 : 1.2,
          mt: isSmallMobile ? 1.4 : 2.5,
          flexWrap: isSmallMobile ? "nowrap" : "wrap",
          overflowX: isSmallMobile ? "auto" : "visible",
        }}
      >
        <Box sx={{ display: "flex", gap: isSmallMobile ? 0.8 : 1, flex: "0 0 auto" }}>
          <Button sx={{ ...outline, ...mobileTourButton }} disabled={step === 0} onClick={onBack}>
            Back
          </Button>
          <Button sx={{ ...outline, ...mobileTourButton }} onClick={onSkip}>
            Skip
          </Button>
        </Box>
        {last ? (
          <Box sx={{ display: "flex", gap: isSmallMobile ? 0.8 : 1, flexWrap: "nowrap" }}>
            <Button sx={{ ...outline, ...mobileTourButton }} onClick={onFinish}>
              Finish
            </Button>
            <Button
              sx={{ ...primary, ...mobileTourButton }}
              onClick={() => navigate("/signup?source=demo-tour")}
            >
              {isSmallMobile ? "Trial" : "Start Free Trial"}
            </Button>
          </Box>
        ) : (
          <Button
            sx={{ ...primary, ...mobileTourButton }}
            onClick={onNext}
            endIcon={actionStep && isSmallMobile ? null : <ArrowForwardIcon />}
          >
            {actionStep ? "Tap button" : "Next"}
          </Button>
        )}
      </Box>
    </GuideCard>
  );
}

export default function DemoDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:1023px)");
  const isSmallMobile = useMediaQuery("(max-width:767px)");
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tab = params.get("tab") || "overview";
  const selected = params.get("finding") || "";
  const focusFix = params.get("focus") === "fix";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [stage, setStage] = useState(stages.EMPTY);
  const [stepIndex, setStepIndex] = useState(0);
  const [tourStep, setTourStep] = useState(0);
  const [setupStep, setSetupStep] = useState(0);
  const [typingProgress, setTypingProgress] = useState(0);
  const [typingStarted, setTypingStarted] = useState(false);
  const [authorised, setAuthorised] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [resolvedCredentials, setResolvedCredentials] = useState({});
  const tourTarget = stage === stages.TOUR ? tourSteps[tourStep]?.target : "";
  const navLocked = [stages.EMPTY, stages.FORM, stages.SCANNING].includes(stage);
  const activeTab = navLocked ? "overview" : tab;
  const typingDone = typingProgress >= typingTotal;
  const typingField =
    !typingStarted || typingDone
      ? ""
      : typingProgress < typedCompany.length
      ? "company"
      : "domains";
  const mobileSetupFocus =
    isSmallMobile && stage === stages.FORM && setupStep === 0 && typingStarted && !typingDone;
  const pageTitle =
    stage === stages.EMPTY
      ? "Live Demo"
      : stage === stages.FORM
      ? "Start Discovery"
      : stage === stages.SCANNING
      ? "Discovery"
      : stage === stages.RESULTS
      ? "Results"
      : stage === stages.TOUR
      ? `Step ${tourStep + 1} of ${tourSteps.length}`
      : nav.find(([key]) => key === activeTab)?.[1] || "Demo";

  useEffect(() => {
    if (stage !== stages.FORM || !typingStarted || typingDone) return undefined;
    const timer = window.setTimeout(() => {
      setTypingProgress((current) => {
        const next = Math.min(typingTotal, current + 1);
        setForm(getTypedForm(next));
        return next;
      });
    }, 55);
    return () => window.clearTimeout(timer);
  }, [stage, typingDone, typingProgress, typingStarted]);

  useEffect(() => {
    if (stage === stages.FORM && typingStarted && typingDone && setupStep === 0) setSetupStep(1);
  }, [setupStep, stage, typingDone, typingStarted]);

  useEffect(() => {
    if (stage === stages.FORM && setupStep === 1 && authorised) setSetupStep(2);
  }, [authorised, setupStep, stage]);

  useEffect(() => {
    if (stage !== stages.FORM || !isSmallMobile) return undefined;
    const targetByStep = {
      0: "company-name-field",
      1: "authorisation-checkbox",
      2: "run-discovery-button",
    };
    const timer = window.setTimeout(() => {
      scrollToDemoTarget(targetByStep[setupStep]);
    }, 150);
    return () => window.clearTimeout(timer);
  }, [isSmallMobile, setupStep, stage]);

  useEffect(() => {
    if (stage !== stages.RESULTS || !isSmallMobile) return undefined;
    const timer = window.setTimeout(() => {
      scrollToDemoTarget("show-matters-first");
    }, 200);
    return () => window.clearTimeout(timer);
  }, [isSmallMobile, stage]);

  useEffect(() => {
    if (stage !== stages.TOUR || !isSmallMobile) return undefined;
    const targetByTourTarget = {
      score: "tour-score",
      "first-finding": "tour-first-finding",
      "view-details": "tour-view-details",
      fix: "tour-fix-button",
      remediation: "tour-remediation",
      "risks-list": "tour-risks-list",
    };
    const timer = window.setTimeout(() => {
      scrollToDemoTarget(targetByTourTarget[tourTarget]);
    }, 260);
    return () => window.clearTimeout(timer);
  }, [isSmallMobile, stage, tourTarget]);

  useEffect(() => {
    if (stage !== stages.SCANNING) return undefined;
    if (stepIndex >= scanSteps.length - 1) {
      const doneTimer = window.setTimeout(() => setStage(stages.RESULTS), 3500);
      return () => window.clearTimeout(doneTimer);
    }
    const timer = window.setTimeout(() => setStepIndex((current) => current + 1), 1850);
    return () => window.clearTimeout(timer);
  }, [stage, stepIndex]);

  useEffect(() => {
    if (navLocked && tab !== "overview") navigate("/demo?tab=overview", { replace: true });
  }, [navLocked, navigate, tab]);

  useEffect(() => {
    if (!navLocked && ["exposures", "breaches"].includes(tab)) {
      navigate("/demo?tab=overview", { replace: true });
    }
  }, [navLocked, navigate, tab]);

  const setRoute = (nextTab, findingId = "", focus = "") => {
    if (navLocked && nextTab !== "overview") return;
    const next = new URLSearchParams();
    next.set("tab", nextTab);
    if (findingId) next.set("finding", findingId);
    if (focus) next.set("focus", focus);
    navigate(`/demo?${next.toString()}`);
    setMobileOpen(false);
  };
  const resetDemo = () => {
    setStage(stages.EMPTY);
    setStepIndex(0);
    setTourStep(0);
    setSetupStep(0);
    setTypingProgress(0);
    setTypingStarted(false);
    setAuthorised(false);
    setForm(blankForm);
    navigate("/demo?tab=overview");
  };
  const beginSetup = () => {
    setStage(stages.FORM);
    setSetupStep(0);
    setTypingProgress(0);
    setTypingStarted(false);
    setAuthorised(false);
    setForm(blankForm);
  };
  const proceedSetup = () => {
    setTypingProgress(0);
    setForm(blankForm);
    setTypingStarted(true);
  };
  const runDiscovery = () => {
    setStepIndex(0);
    setStage(stages.SCANNING);
  };
  const startTour = () => {
    setTourStep(0);
    setStage(stages.TOUR);
    setRoute("overview");
  };
  const nextTour = () => {
    if (tourStep === 3) {
      scrollToDemoTarget("tour-fix-button");
      return;
    }
    if (tourStep === 4) setRoute("risks");
    if (tourStep >= tourSteps.length - 1) setStage(stages.COMPLETE);
    else setTourStep((current) => current + 1);
  };
  const previousTour = () => {
    if (tourStep === 5) setRoute("risks", demoFindings[0].id, "fix");
    if (tourStep === 4) setRoute("overview");
    setTourStep((current) => Math.max(0, current - 1));
  };

  const content = {
    overview: (
      <Overview
        stage={stage}
        form={form}
        setForm={setForm}
        authorised={authorised}
        setAuthorised={setAuthorised}
        onStart={beginSetup}
        onRun={runDiscovery}
        stepIndex={stepIndex}
        onFastForward={() => setStage(stages.RESULTS)}
        goRisk={(id, focus) => setRoute("risks", id, focus)}
        navigate={navigate}
        onTour={startTour}
        tourTarget={tourTarget}
        onTourFix={() => setTourStep(4)}
        setupStep={setupStep}
        typingStarted={typingStarted}
        typingField={typingField}
        typingDone={typingDone}
        onProceed={proceedSetup}
        isSmallMobile={isSmallMobile}
      />
    ),
    risks: (
      <Risks
        selectedId={selected}
        focusFix={focusFix}
        select={(id) => setRoute("risks", id)}
        back={() => setRoute("risks")}
        tourTarget={tourTarget}
      />
    ),
    assets: <AssetsPage />,
    credentials: (
      <CredentialsPage resolved={resolvedCredentials} setResolved={setResolvedCredentials} />
    ),
    monitoring: <MonitoringPage goRisks={() => setRoute("risks")} />,
    reports: <ReportsPage />,
    settings: <SettingsPage onReset={resetDemo} navigate={navigate} />,
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: bg,
        color: "#fff",
        display: "flex",
        "@keyframes ndPulse": {
          "0%": { transform: "scale(0.72)", opacity: 0.9 },
          "70%": { transform: "scale(1.35)", opacity: 0 },
          "100%": { transform: "scale(1.35)", opacity: 0 },
        },
        "@keyframes ndNodePulse": {
          "0%, 100%": {
            boxShadow: "0 0 0 10px rgba(95,243,255,0.05), 0 0 60px rgba(95,243,255,0.22)",
          },
          "50%": { boxShadow: "0 0 0 18px rgba(95,243,255,0.02), 0 0 90px rgba(95,243,255,0.38)" },
        },
        "@keyframes ndNodePop": {
          "0%": { transform: "scale(0.35)", opacity: 0 },
          "70%": { transform: "scale(1.18)", opacity: 1 },
          "100%": { transform: "scale(1)", opacity: 1 },
        },
        "@keyframes ndCtaPulse": {
          "0%": {
            boxShadow: "0 0 0 0 rgba(95,243,255,0.28), inset 0 1px 0 rgba(255,255,255,0.16)",
          },
          "70%": {
            boxShadow: "0 0 0 12px rgba(95,243,255,0), inset 0 1px 0 rgba(255,255,255,0.16)",
          },
          "100%": { boxShadow: "0 0 0 0 rgba(95,243,255,0), inset 0 1px 0 rgba(255,255,255,0.16)" },
        },
      }}
    >
      {!isMobile && (
        <Sidebar
          current={activeTab}
          onChange={setRoute}
          tourTarget={tourTarget}
          locked={navLocked}
        />
      )}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          px: mobileSetupFocus ? 1.4 : { xs: 2, md: 3.2 },
          py: mobileSetupFocus ? 1.5 : { xs: 2.5, md: 4 },
          pb: 8,
        }}
      >
        {isMobile && (
          <MobileHeader
            title={pageTitle}
            onMenu={() => setMobileOpen(true)}
            hidden={mobileSetupFocus}
          />
        )}
        {content[activeTab] || content.overview}
      </Box>
      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{ sx: { background: "#061020", color: "#fff" } }}
      >
        <Sidebar
          current={activeTab}
          onChange={setRoute}
          mobile
          tourTarget={tourTarget}
          locked={navLocked}
        />
      </Drawer>
      {stage === stages.TOUR && (
        <TourOverlay
          step={tourStep}
          onBack={previousTour}
          onNext={nextTour}
          onSkip={() => setStage(stages.COMPLETE)}
          onFinish={() => setStage(stages.COMPLETE)}
          navigate={navigate}
          isSmallMobile={isSmallMobile}
        />
      )}
    </Box>
  );
}
